// /home/heliowallet/frontend/services/rpcPoolManager.ts
import { ethers } from 'ethers';

interface RpcEndpoint {
  url: string;
  provider: ethers.JsonRpcProvider;
  failCount: number;
  lastFailTime: number;
  lastUseTime: number;
  isAvailable: boolean;
  currentDelay: number;
  totalRequests: number;
  successfulRequests: number;
  health: {
    latency: number;
    score: number;
    lastCheck: number;
  };
}

interface Task<T> {
  id: string;
  execute: (provider: ethers.JsonRpcProvider) => Promise<T>;
  lastError?: Error;
}

interface TaskResult<T> {
  id: string;
  success: boolean;
  data?: T;
  error?: Error;
}

interface BulkConfig {
  rpcRotateInterval: number; // Minimum time between uses of same RPC
  maxConcurrent: number;
  timeout: number;
}

export class RpcPoolManager {
  private endpoints: RpcEndpoint[] = [];
  private readonly MIN_DELAY = 500;      // 500ms minimum delay for failed RPC
  private readonly MAX_DELAY = 30000;    // 30s maximum delay
  private readonly FAIL_PENALTY = 1.5;   // Tăng delay 1.5x mỗi lần fail
  private readonly RECOVERY_TIME = 60000; // 60s để reset fail count
  private readonly MIN_ROTATE_TIME = 1000; // 1s minimum between uses of same RPC
  
  private lastRpcIndex = 0;
  private requestCount = 0;
  private healthCheckInterval: NodeJS.Timeout | null = null;
  
  constructor(rpcUrls: string[]) {
    // Shuffle RPCs để phân bố tải ngẫu nhiên
    const shuffled = [...rpcUrls].sort(() => Math.random() - 0.5);
    
    this.endpoints = shuffled.map(url => {
      // Create provider without any network or polling
      const provider = new ethers.JsonRpcProvider(url);
      
      // Disable all automatic features properly
      this.disablePolling(provider);
      
      return {
        url,
        provider,
        failCount: 0,
        lastFailTime: 0,
        lastUseTime: 0,
        isAvailable: true,
        currentDelay: 0,
        totalRequests: 0,
        successfulRequests: 0,
        health: {
          latency: 0,
          score: 50,
          lastCheck: 0
        }
      };
    });
    
    // Start health check interval
    this.startHealthChecks();
  }

  private disablePolling(provider: ethers.JsonRpcProvider) {
    // Set polling interval to a very high value to effectively disable it
    (provider as any).pollingInterval = 999999999;
    (provider as any)._pollingInterval = 999999999;
    
    // Stop any active polling
    provider.removeAllListeners('block');
    provider.removeAllListeners('network');
    
    // Override internal polling flag
    (provider as any).polling = false;
    
    // Remove listeners
    provider.removeAllListeners();
    
    // Prevent future polling
    (provider as any)._setTimeout = () => {};
    (provider as any)._clearTimeout = () => {};
    
    // Disable automatic network detection
    (provider as any)._networkPromise = null;
    (provider as any)._network = null;
  }

  private startHealthChecks() {
    // Check health every 2 minutes
    this.healthCheckInterval = setInterval(() => {
      this.checkEndpointsHealth();
    }, 2 * 60 * 1000);
  }

  private async checkEndpointsHealth() {
    const healthPromises = this.endpoints.map(async (endpoint) => {
      if (!endpoint.isAvailable) return;
      
      try {
        const startTime = Date.now();
        await endpoint.provider.getBlockNumber();
        const latency = Date.now() - startTime;
        
        // Update health score
        let score = 100;
        if (latency > 500) score -= 10;
        if (latency > 1000) score -= 20;
        if (latency > 2000) score -= 30;
        if (latency > 3000) score -= 30;
        
        endpoint.health = {
          latency,
          score: Math.max(score, 10),
          lastCheck: Date.now()
        };
        
        // Reset fail count on success
        if (endpoint.failCount > 0) {
          endpoint.failCount = 0;
          endpoint.currentDelay = 0;
        }
        
      } catch (error) {
        // Update health on failure
        endpoint.health = {
          latency: Infinity,
          score: 0,
          lastCheck: Date.now()
        };
      }
    });
    
    await Promise.all(healthPromises);
  }

  // Execute single task
  async executeTask<T>(task: Task<T>): Promise<TaskResult<T>> {
    const endpoint = await this.getNextEndpoint();
    
    if (!endpoint) {
      console.error(`No available RPC for task ${task.id}`);
      return {
        id: task.id,
        success: false,
        error: new Error('No available RPC endpoints')
      };
    }
    
    // Log RPC usage
    this.requestCount++;
    endpoint.totalRequests++;
    endpoint.lastUseTime = Date.now();
    
    try {
      const startTime = Date.now();
      const data = await task.execute(endpoint.provider);
      const duration = Date.now() - startTime;
      
      // Success - update stats
      endpoint.successfulRequests++;
      endpoint.failCount = 0;
      endpoint.currentDelay = 0;
      
      // Update health
      endpoint.health = {
        latency: duration,
        score: Math.min(100, endpoint.health.score + 5),
        lastCheck: Date.now()
      };
      
      // Success logging removed for production
      
      return {
        id: task.id,
        success: true,
        data
      };
      
    } catch (error: any) {
      // Failure - update endpoint stats
      endpoint.failCount++;
      endpoint.lastFailTime = Date.now();
      endpoint.currentDelay = Math.min(
        this.MIN_DELAY * Math.pow(this.FAIL_PENALTY, endpoint.failCount - 1),
        this.MAX_DELAY
      );
      
      // Update health
      endpoint.health = {
        latency: Infinity,
        score: Math.max(0, endpoint.health.score - 20),
        lastCheck: Date.now()
      };
      
      console.error(`Task ${task.id} failed on ${endpoint.url}:`, error.message);
      
      // Mark endpoint as unavailable if too many fails
      if (endpoint.failCount >= 5) {
        endpoint.isAvailable = false;
        console.warn(`RPC ${endpoint.url} marked as unavailable after ${endpoint.failCount} failures`);
      }
      
      task.lastError = error;
      
      return {
        id: task.id,
        success: false,
        error
      };
    }
  }

  // Execute multiple tasks with load balancing
  async executeBulkTasks<T>(
    tasks: Task<T>[],
    config: BulkConfig = {
      rpcRotateInterval: 1000,
      maxConcurrent: 5,
      timeout: 10000
    }
  ): Promise<TaskResult<T>[]> {
    const results: TaskResult<T>[] = [];
    const semaphore = new Array(config.maxConcurrent).fill(null);
    
    const executeWithSemaphore = async (task: Task<T>): Promise<TaskResult<T>> => {
      const semaphoreIndex = semaphore.findIndex(s => s === null);
      if (semaphoreIndex === -1) {
        // Wait for a slot to become available
        await new Promise(resolve => {
          const checkSlot = () => {
            const index = semaphore.findIndex(s => s === null);
            if (index !== -1) {
              semaphore[index] = task.id;
              resolve(undefined);
            } else {
              setTimeout(checkSlot, 10);
            }
          };
          checkSlot();
        });
      } else {
        semaphore[semaphoreIndex] = task.id;
      }
      
      try {
        const result = await this.executeTask(task);
        return result;
      } finally {
        const index = semaphore.findIndex(s => s === task.id);
        if (index !== -1) {
          semaphore[index] = null;
        }
      }
    };
    
    // Execute tasks with timeout
    const taskPromises = tasks.map(task => 
      Promise.race([
        executeWithSemaphore(task),
        new Promise<TaskResult<T>>((_, reject) => 
          setTimeout(() => reject(new Error('Task timeout')), config.timeout)
        )
      ]).catch(error => ({
        id: task.id,
        success: false,
        error
      }))
    );
    
    const taskResults = await Promise.all(taskPromises);
    results.push(...taskResults);
    
    return results;
  }
  
  // Get next available endpoint with rotation
  private async getNextEndpoint(): Promise<RpcEndpoint | null> {
    const now = Date.now();
    
    // Check for recovery
    this.endpoints.forEach(endpoint => {
      if (!endpoint.isAvailable && 
          endpoint.lastFailTime > 0 &&
          now - endpoint.lastFailTime > this.RECOVERY_TIME) {
        endpoint.isAvailable = true;
        endpoint.failCount = 0;
        endpoint.currentDelay = 0;
      }
    });
    
    // Get available endpoints
    const availableEndpoints = this.endpoints.filter(ep => {
      // Check if available
      if (!ep.isAvailable) return false;
      
      // Check if enough time passed since last use
      if (now - ep.lastUseTime < this.MIN_ROTATE_TIME) return false;
      
      // Check if still in delay period
      if (ep.currentDelay > 0 && now - ep.lastFailTime < ep.currentDelay) return false;
      
      return true;
    });
    
    if (availableEndpoints.length === 0) {
      // If no endpoints available, wait and try the one with shortest delay
      const sortedByDelay = this.endpoints
        .filter(ep => ep.isAvailable)
        .sort((a, b) => {
          const aWaitTime = Math.max(0, a.currentDelay - (now - a.lastFailTime));
          const bWaitTime = Math.max(0, b.currentDelay - (now - b.lastFailTime));
          return aWaitTime - bWaitTime;
        });
      
      if (sortedByDelay.length > 0) {
        const bestEndpoint = sortedByDelay[0];
        const waitTime = Math.max(0, bestEndpoint.currentDelay - (now - bestEndpoint.lastFailTime));
        
        if (waitTime > 0) {
          await this.delay(waitTime);
        }
        
        bestEndpoint.lastUseTime = Date.now();
        return bestEndpoint;
      }
      
      return null;
    }
    
    // Sort by health score and use round-robin for equal scores
    availableEndpoints.sort((a, b) => {
      if (b.health.score !== a.health.score) {
        return b.health.score - a.health.score;
      }
      return a.lastUseTime - b.lastUseTime;
    });
    
    const selectedEndpoint = availableEndpoints[0];
    selectedEndpoint.lastUseTime = Date.now();
    
    return selectedEndpoint;
  }

  // Get endpoint statistics
  getEndpointStats() {
    return this.endpoints.map(ep => ({
      url: ep.url,
      isAvailable: ep.isAvailable,
      failCount: ep.failCount,
      totalRequests: ep.totalRequests,
      successfulRequests: ep.successfulRequests,
      successRate: ep.totalRequests > 0 ? (ep.successfulRequests / ep.totalRequests) * 100 : 0,
      health: ep.health,
      lastUseTime: ep.lastUseTime,
      currentDelay: ep.currentDelay
    }));
  }

  // Get best endpoint by health score
  getBestEndpoint(): RpcEndpoint | null {
    const availableEndpoints = this.endpoints.filter(ep => ep.isAvailable);
    
    if (availableEndpoints.length === 0) return null;
    
    return availableEndpoints.reduce((best, current) => 
      current.health.score > best.health.score ? current : best
    );
  }

  // Force health check on all endpoints
  async forceHealthCheck(): Promise<void> {
    await this.checkEndpointsHealth();
  }

  // Reset endpoint failure state
  resetEndpoint(url: string): void {
    const endpoint = this.endpoints.find(ep => ep.url === url);
    if (endpoint) {
      endpoint.failCount = 0;
      endpoint.currentDelay = 0;
      endpoint.isAvailable = true;
      endpoint.lastFailTime = 0;
    }
  }

  // Add new endpoint
  addEndpoint(url: string): void {
    const provider = new ethers.JsonRpcProvider(url);
    this.disablePolling(provider);
    
    this.endpoints.push({
      url,
      provider,
      failCount: 0,
      lastFailTime: 0,
      lastUseTime: 0,
      isAvailable: true,
      currentDelay: 0,
      totalRequests: 0,
      successfulRequests: 0,
      health: {
        latency: 0,
        score: 50,
        lastCheck: 0
      }
    });
  }

  // Remove endpoint
  removeEndpoint(url: string): void {
    const index = this.endpoints.findIndex(ep => ep.url === url);
    if (index !== -1) {
      this.endpoints.splice(index, 1);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Cleanup
  destroy(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
    
    this.endpoints.forEach(ep => {
      try {
        ep.provider.destroy();
      } catch (error) {
        console.error('Error destroying provider:', error);
      }
    });
    
    this.endpoints = [];
  }
}